import Order from "@/modules/delivery/core/models/Order";
import OrderSystem from "@/modules/delivery/core/OrderSystem";
import CalendarProvider from "@/modules/calendar/providers/CalendarProvider";
import CalendarModel from "@/modules/calendar/core/models/CalendarModel";
import OrderSettings from "@/modules/delivery/core/OrderSettings";
import CategoryService from "@/modules/delivery/core/services/CategoryService";
import OrderProvider from "@/modules/delivery/providers/OrderProvider";
import Branch from "@/modules/delivery/core/models/Branch";
import CouponProvider from "@/modules/delivery/providers/CouponProvider";


export default {
    state: {
        order: null,
        orderSystem: new OrderSystem(),
        editing: false,
        locationHistory: [],
        lastContact: {name: '', phone: '', email:'', observations:''},
        galleryView: 4
    },
    getters: {
        hasOrder(state) {
            return state.order !== null
        },
        orderSystem(state) {
            return state.orderSystem;
        },
        deliveryTime(state) {
            return state.order.getTime
        },
        deliveryDate(state) {
            return state.order.getDate
        },
        galleryView(state) {
            return state.galleryView
        },
        lastContact(state){
            return state.lastContact
        },
        orderIdentifier(state){
            return state.orderSystem.getOrder.identifier
        },
        orderState(state){
            return state.orderSystem.getOrder.state
        },
        editing(state){
            return state.editing
        }
    },
    actions: {
        async fetchCalendar(_, identifier) {
            let r = await CalendarProvider.findCalendarByIdentifier(identifier)
            return r.data.calendarFindByIdentifier
        },
        setupOrderSystem({dispatch,commit}) {
            dispatch('setupOrderSettings')
            dispatch('setupCalendars')
            dispatch('setupCategoryService')
            dispatch('setupBranch')
            dispatch('setupPaymentMethods')
            commit('linkLocationHistory')
        },
        async setupOrderSettings({commit, getters, dispatch}) {
            if (!getters.isSettingsReady) {
                setTimeout(() => dispatch('setupOrderSettings'), 300)
                return
            }
            let settings = {}
            settings.minimumQuantity = getters.getSettingValue('MinimumQuantity')
            settings.intervalDeliveryTime = getters.getSettingValue('IntervalDeliveryTime')
            settings.intervalTakeAwayTime = getters.getSettingValue('IntervalTakeAwayTime')
            settings.preparationTime = getters.getSettingValue('PreparationTime')
            settings.rangeScheduleTime = getters.getSettingValue('RangeScheduleTime')
            settings.deliveryTime = getters.getSettingValue('DeliveryTime')
            settings.deliveryTimeLongDistance = getters.getSettingValue('DeliveryTimeLongDistance')
            settings.extendedBarTime = getters.getSettingValue('ExtendedBarTime')
            commit("setOrderSettings", settings)
            return
        },
        async setupCalendars({commit, dispatch}) {
            let takeAwayCalendar = await dispatch('fetchCalendar', 'take-away')
            let deliveryCalendar = await dispatch('fetchCalendar', 'delivery')
            commit('setCalendarTakeAway', takeAwayCalendar)
            commit('setCalendarDelivery', deliveryCalendar)
            return
        },
        async setupBranch({commit, dispatch, getters}) {
            if(getters.branchSelected){
                let branch = getters.branchSelected
                commit('setBranch', branch)
            }else{
                let branch = await dispatch('fetchDefaultBranch')
                commit('setBranch', branch)
            }
        },
        async setupCategoryService({commit, dispatch}) {
            let categories = await dispatch('fetchCategories')
            commit('setCategoryService', categories)
        },
        async setupPaymentMethods({commit, dispatch}) {
            let paymentMethods = await dispatch('findPaymentMethods')
            commit('setSystemPaymentMethods', paymentMethods)
        },
        initOrder({commit}, {origin}) {
            let order = {}
            if (origin) {
                order.origin = origin
            }
            commit('setupOrder', order)
        },
        createOrder({commit, getters}) {
            return new Promise((resolve, reject) => {
                commit("orderError", null)
                OrderProvider.createOrder(getters.orderSystem.getOrderForm)
                    .then(r => {
                        commit("setupOrder", r.data.orderCreate)
                        resolve(r.data.orderCreate)
                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject(e)
                    })

            })

        },
        updateOrder({commit, getters}) {
            return new Promise((resolve, reject) => {
                commit("orderError", null)
                OrderProvider.updateOrder(getters.orderSystem.order.id, getters.orderSystem.getOrderForm)
                    .then(r => {
                        commit("setupOrder", r.data.orderUpdate)
                        resolve(r.data.orderUpdate)
                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject(e)
                    })

            })
        },
        findOrderByIdentifier({commit, dispatch}, identifier) {
            return new Promise((resolve, reject) => {
                OrderProvider.orderFindByIdentifier(identifier)
                    .then(r => {
                        const order = r.data.orderFindByIdentifier
                        if(order){
                            commit('setupOrder', order)
                            commit('orderIdentifier', identifier)
                            if(order.couponCode){
                                dispatch('findCouponByCode',order.couponCode)
                            }
                            resolve(order)
                        }else{
                            commit("clearOrder")
                            reject('Order not found')
                        }

                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject(e)
                    })
            })
        },
        refreshOrderStateByIdentifier({commit, getters}, identifier) {
            return new Promise((resolve, reject) => {
                OrderProvider.orderStateFindByIdentifier(identifier)
                    .then(r => {
                        commit('orderState', r.data.orderFindByIdentifier.state)
                        resolve(getters.getOrder)
                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject(e)
                    })
            })
        },
        updateOrderReceiptFile({commit, getters}, receiptFile) {
            return new Promise((resolve, reject) => {
                commit('paymentReceiptFile', receiptFile)
                OrderProvider.updateOrderReceiptFile({
                    orderId: getters.orderSystem.order.id,
                    receiptFile: receiptFile
                })
                    .then(r => {
                        commit('orderState', r.data.orderUpdateReceiptFile.state)
                        commit('paymentConfirmed', r.data.orderUpdateReceiptFile.payment.confirmed)
                        resolve(r.data.orderUpdateReceiptFile)
                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject()
                    })
            })
        },
        updateOrderPaymentMethod({commit, getters}, paymentMethod) {
            return new Promise((resolve, reject) => {

                OrderProvider.updateOrderPaymentMethod({
                    orderId: getters.orderSystem.order.id,
                    paymentMethod: paymentMethod
                })
                    .then(r => {
                        commit('paymentMethod', r.data.orderUpdatePaymentMethod.payment.method)
                        commit('orderState', r.data.orderUpdatePaymentMethod.state)
                        resolve(r.data.orderUpdatePaymentMethod)
                    })
                    .catch(e => {
                        commit("orderError", e.message)
                        reject()
                    })
            })
        },
        async findCouponByCode({commit}, code) {
            let coupon = null
            if(code){
                let r = await CouponProvider.findCouponByCode(code)
                coupon = r.data.findCouponByCode
            }
            commit("applyCoupon", coupon)
            return coupon
        },

    },
    mutations: {
        setupOrder(state, order) {
            state.order = new Order(order)
            state.orderSystem.setOrder(state.order)
        },
        setEditing(state, editing){
            state.editing = editing
        },
        setOrderSettings(state, orderSettings) {
            state.orderSystem.setSettings(new OrderSettings(orderSettings))
        },
        setCategoryService(state, categories) {
            state.orderSystem.setCategoryService(new CategoryService(categories))
        },
        setSystemPaymentMethods(state, paymentMethods) {
            state.orderSystem.setPaymentMethods(paymentMethods)
        },
        setCalendarTakeAway(state, calendar) {
            state.orderSystem.setCalendarTakeAway(new CalendarModel(calendar));
        },
        setCalendarDelivery(state, calendar) {
            state.orderSystem.setCalendarDelivery(new CalendarModel(calendar));
        },
        setBranch(state, branch) {
            state.orderSystem.setBranch(new Branch(branch))
        },
        applyCoupon(state, coupon) {
            state.orderSystem.applyCoupon(coupon)
        },
        clearCoupon(state, coupon) {
            state.orderSystem.clearCoupon(coupon)
        },
        linkLocationHistory(state) {
            state.orderSystem.setLocationHistory(state.locationHistory)
        },
        orderDeliveryMode(state, deliveryMode) {
            state.orderSystem.setMode(deliveryMode);
        },
        orderTable(state, table) {
            state.orderSystem.setTable(table);
        },
        orderTimeMode(state, timeMode) {
            state.orderSystem.setTimeMode(timeMode);
        },
        orderTime(state, time) {
            state.orderSystem.setTime(time);
        },
        orderDate(state, date) {
            state.orderSystem.setDate(date);
        },
        addItem(state, {product, category, order}) {
            state.orderSystem.addOrderItem(product, category, order)
        },
        changeItemQuantity(state, {product, category, quantity, order}){
            state.orderSystem.changeOrderItemQuantity(product, category, quantity, order)
        },
        removeItem(state, {product, category, order}) {
            state.orderSystem.removeOrderItem(product, category, order)
        },
        removeOrderPromo(state,promo){
            state.orderSystem.removeOrderPromo(promo)
        },
        clearItems(state) {
            state.orderSystem.clearItems()
        },
        clearOrder(state){
            state.editing = false
            state.order = null
            state.orderSystem.order = null
        },
        setOrigin(state, origin) {
          state.orderSystem.setOrderOrigin(origin)
        },
        orderContact(state, {name, phone, email, observations}) {
            state.orderSystem.contactService.setOrderContact({name, phone, email, observations})
        },
        lastContact(state, {name, phone, email, observations}) {
            state.lastContact = {name: name, phone: phone, email: email, observations: observations}
        },
        paymentMethod(state, paymentMethod) {
            state.orderSystem.setPaymentMethod(paymentMethod)
        },
        couponCode(state, couponCode) {
            state.orderSystem.setCouponCode(couponCode)
        },
        clearCouponInvalid(state){
            state.orderSystem.couponInvalid = false
        },
        paymentReceiptFile(state, paymentReceiptFile) {
            state.orderSystem.setReceiptFile(paymentReceiptFile)
        },
        paymentConfirmed(state, paymentConfirmed) {
            state.orderSystem.setPaymentConfirmed(paymentConfirmed)
        },
        onBar(state, val) {
          state.orderSystem.setOnBar(val)
        },
        orderDiscountRate(state, rate) {
            state.orderSystem.setDiscountRate(rate)
        },
        orderDiscountAmount(state, amount) {
            state.orderSystem.setDiscountAmount(amount)
        },
        orderDeliveryFree(state, deliveryFree) {
            state.orderSystem.setDeliveryFree(deliveryFree)
        },
        orderEmployee(state, employee) {
            state.orderSystem.setEmployee(employee)
        },
        galleryView(state, val) {
            state.galleryView = val
        },
        orderError(state, error) {
            state.orderSystem.setOrderError(error)
        },
        orderId(state, id) {
            state.orderSystem.setOrderId(id)
        },
        orderIdentifier(state, identifier) {
            state.orderSystem.setOrderIdentifier(identifier)
        },
        orderState(state, val) {
            state.orderSystem.setOrderState(val)
        },
        orderDeliveryCost(state, cost) {
            state.orderSystem.order.delivery.cost = cost
        },
        orderDeliveryDistance(state, distance) {
            state.orderSystem.order.delivery.distance = distance
        },
        orderDeliveryLongDistance(state, longDistance) {
            state.orderSystem.order.delivery.longDistance = longDistance
        },
        clearOrderLocation(state) {
            state.orderSystem.locationService.clear()
        },
        orderLocation(state, location){
            state.orderSystem.locationService.setLocation(location)
        },
        addLocationHistory(state, location) {
            state.orderSystem.locationHistoryService.addLocationHistory(location)
        },
        removeLocationHistory(state, location){
            state.orderSystem.locationHistoryService.removeLocationHistory(location)
        }
    }
}
